import { useEffect, useState } from 'react';
import { CopyIcon } from '@icons';

import Tooltip from '@components/tooltip/Tooltip';

interface CopyTextProps {
  textToCopy: string;
  textId?: string;
  className?: string;
  iconColor?: string;
  isDisabled?: boolean;
  onCopy?: () => void;
  text?: string;
  textStyle?: string;
  imagePath?: string;
  tooltipClassName?: string;
}

const CopyToClipboard = (props: CopyTextProps) => {
  const {
    textToCopy,
    textId = '',
    className = '',
    iconColor = 'text-borderGrey2',
    isDisabled,
    onCopy,
    text = '',
    textStyle,
    imagePath = '',
    tooltipClassName = ''
  } = props;

  const [showTooltip, setShowTooltip] = useState<boolean>(false);

  const handleClick = async (e: React.MouseEvent) => {
    setShowTooltip(true);
    if (imagePath) {
      try {
        const response = await fetch(imagePath);
        const blob = await response.blob();

        const clipboardItem = new ClipboardItem({
          [blob.type]: blob,
          'text/plain': new Blob([textToCopy], { type: 'text/plain' })
        });
        await navigator.clipboard?.write([clipboardItem]);
      } catch (error) {
        navigator.clipboard?.writeText(textToCopy);
      }
    } else {
      navigator.clipboard.writeText(textToCopy);
    }
    if (onCopy) onCopy();
    e.stopPropagation();
  };

  // how to clean up the timer
  useEffect(() => {
    const timer = setTimeout(() => {
      if (showTooltip) setShowTooltip(false);
    }, 500);

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [showTooltip]);

  return (
    <div
      role="presentation"
      className={`group/tooltip relative flex ${
        isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
      } ${className}`}
      onClick={handleClick}
      data-tooltip-id={`copy-tooltip-${textId || textToCopy}`}
    >
      <CopyIcon
        className={`${iconColor} ${isDisabled ? '' : 'hover:text-grey'}`}
      />
      {text.trim().length > 0 && (
        <div className={`ml-3 text-base font-medium text-primary ${textStyle}`}>
          {text}
        </div>
      )}
      {!isDisabled && (
        <Tooltip
          id={`copy-tooltip-${textId || textToCopy}`}
          type="text"
          showTooltip={showTooltip}
          tooltipText="Copied!"
          className={`${tooltipClassName} ${
            showTooltip ? '!opacity-100' : '!opacity-0'
          }`}
        />
      )}
    </div>
  );
};

export default CopyToClipboard;
